/* TODO(ASIMO-2105): cherrera2@ to remove once experiment is over regardless of outcome */

import { type DataSourceConnectionDetails } from '../../connectedDataSource';
import { getItem, removeItem, setItem } from '../localStorage';

/** Default expiration time for third-party connection status cache (15 minutes in milliseconds) */
export const DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS = 1000 * 60 * 15; // 15 minutes in milliseconds

/**
 * Represents the cached status of a third-party connection with expiration
 */
export type ThirdPartyConnectionStatus = {
	/** Unix timestamp in milliseconds when this cache entry expires */
	expirationDate: number;
	/** Whether the user has completed OAuth authentication for this data source */
	isUserOAuthed: boolean;
	/** URL for initiating OAuth flow for this data source connection */
	outboundAuthUrl: string;
};

/** Represents the Google Drive banner status */
export type GoogleDriveBannerStatus = {
	/** Whether the banner has been dismissed */
	hasBannerBeenDismissed: boolean;
};

/** Storage key for persisting Google Drive connection status in localStorage */
export const GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY =
	'link-picker-google-drive-experiment-third-party-connection-status';
export const GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY =
	'link-picker-google-drive-experiment-third-party-connection-banner';

/**
 * Retrieves Google Drive connection status from localStorage with expiration check
 * @returns The cached connection status, or null if not found or expired
 */
export const getGoogleDriveConnectionStatus = (): ThirdPartyConnectionStatus | null => {
	const status = getItem<ThirdPartyConnectionStatus>(GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY);
	// Check if the cached status has expired
	if (status && status.expirationDate <= Date.now()) {
		// Remove expired entry and return null
		removeItem(GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY);
		return null;
	}
	return status;
};

/**
 * Stores Google Drive connection status in localStorage with expiration timestamp
 * @param integrationDetails - The data source connection details from the server
 */
export const setGoogleDriveConnectionStatus = (
	integrationDetails: DataSourceConnectionDetails,
): void => {
	const { isUserOAuthed, outboundAuthUrl } = integrationDetails;
	const status: ThirdPartyConnectionStatus = {
		// Set expiration time based on current time + default TTL
		expirationDate: Date.now() + DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
		isUserOAuthed,
		outboundAuthUrl,
	};
	setItem(GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY, status);
};

/**
 * Gets Google Drive connection banner status in localStorage
 * @returns The cached banner status, or null if not found
 */
export const getGoogleDriveConnectionBannerStatus = (): GoogleDriveBannerStatus | null => {
	const status = getItem<GoogleDriveBannerStatus>(GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY);
	return status ?? null;
};

/**
 * Stores Google Drive connection banner status in localStorage
 * @param hasBannerBeenDismissed - Whether the banner has been dismissed
 */
export const setGoogleDriveConnectionBanner = (hasBannerBeenDismissed: boolean): void => {
	setItem(GOOGLE_DRIVE_CONNECTION_BANNER_STORAGE_KEY, { hasBannerBeenDismissed });
};
